import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Header from '../../layouts/Header';
import { Container, Spinner, Form } from 'react-bootstrap';
import axiosInstance from '../../api/axios';
import OutletSelect from '../../components/OutletSelect'; // adjust path as needed

/* ─── constants ──────────────────────────────────────────── */
const STATUS_CONFIG = {
    PENDING:   { label: 'Pending',   color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
    ACCEPTED:  { label: 'Accepted',  color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED:  { label: 'Rejected',  color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
    FULFILLED: { label: 'Fulfilled', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
};

// API only allows setting these two statuses
const UPDATABLE_STATUSES = {
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};

const fmt = (iso) =>
    new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

/* ─── StatusPill ─────────────────────────────────────────── */
const StatusPill = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' };
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
            whiteSpace: 'nowrap',
        }}>
            {cfg.label}
        </span>
    );
};

/* ─── Items Expandable ───────────────────────────────────── */
const ItemsRow = ({ items }) => {
    const [open, setOpen] = useState(false);
    return (
        <div style={{ marginTop: 10 }}>
            <button onClick={() => setOpen(o => !o)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#6366f1', fontSize: 13, fontWeight: 600,
            }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: 6,
                    background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 800,
                }}>
                    {items.length}
                </span>
                Item{items.length !== 1 ? 's' : ''}
                <span style={{
                    fontSize: 9, display: 'inline-block',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s',
                }}>▼</span>
            </button>

            {open && (
                <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{
                        display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr 1fr 130px',
                        background: '#f9fafb', padding: '7px 14px', borderBottom: '1px solid #e5e7eb',
                        fontSize: 11, fontWeight: 700, color: '#6b7280',
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>
                        <span>Material</span><span>Category</span><span>HSN</span><span>Unit</span>
                        <span>Requested Qty</span>
                    </div>
                    {items.map((item, idx) => {
                        const rm = item.rawMaterial || {};
                        return (
                            <div key={item._id || idx} style={{
                                display: 'grid', gridTemplateColumns: '2.5fr 1fr 1fr 1fr 130px',
                                padding: '9px 14px', alignItems: 'center',
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                                fontSize: 13,
                            }}>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>{rm.name || '—'}</div>
                                    {rm.description && (
                                        <div style={{
                                            color: '#9ca3af', fontSize: 11, marginTop: 1,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300,
                                        }}>{rm.description}</div>
                                    )}
                                </div>
                                <span style={{ color: '#373b41', fontSize: 12 }}>{rm.subCategory?.name} <br />
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>{rm.mainCategory?.name || '—'}</div>
                                
                                </span>
                                <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{rm.hsn || '—'}</span>
                                <span style={{ color: '#6b7280', fontSize: 12 }}>{rm.unit || '—'}</span>
                                <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                    {item.requestedQuantity} <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{rm.unit || ''}</span>
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ─── Update Modal ───────────────────────────────────────── */
const UpdateModal = ({ request, onHide, onUpdated, accessToken }) => {
    const [status, setStatus] = useState('ACCEPTED');
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState('');

    // Request is already finalised if it's ACCEPTED or REJECTED
    const alreadyUpdated = request.status === 'ACCEPTED' || request.status === 'REJECTED';

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            // PUT method | body: { _id, status } | only ACCEPTED or REJECTED allowed
            await axiosInstance.put(`stock-requisition/update-status`, { _id: request._id, status }, { headers });
            onUpdated(request._id, status);
            onHide();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1050,
            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onHide}>
            <div style={{
                background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
                margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>

                {/* header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
                }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>Update Stock Request</span>
                    <button onClick={onHide} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0,
                    }}>×</button>
                </div>

                {/* body */}
                <div style={{ padding: '20px' }}>

                    {/* indent + current status info */}
                    <div style={{
                        marginBottom: 16, padding: '10px 14px',
                        background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Indent Number
                            </div>
                            <div style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace', fontSize: 14 }}>
                                {request.indentNumber}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Current Status
                            </div>
                            <StatusPill status={request.status} />
                        </div>
                    </div>

                    {alreadyUpdated ? (
                        /* already finalised — cannot change */
                        <div style={{
                            padding: '16px', borderRadius: 9,
                            background: '#f9fafb', border: '1px solid #e5e7eb',
                            textAlign: 'center', color: '#6b7280', fontSize: 13, lineHeight: 1.6,
                        }}>
                            This request has already been{' '}
                            <strong style={{ color: '#374151' }}>{request.status.toLowerCase()}</strong>{' '}
                            and cannot be changed further.
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                Set Status{' '}
                                <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(choose one)</span>
                            </div>
                            {/* side-by-side ACCEPTED / REJECTED radio cards */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
                                    <label key={key} style={{
                                        flex: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 8,
                                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                                        border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`,
                                        background: status === key ? cfg.bg : '#fff',
                                        transition: 'all .15s', textAlign: 'center',
                                    }}>
                                        <input
                                            type="radio" name="status" value={key}
                                            checked={status === key}
                                            onChange={() => setStatus(key)}
                                            style={{ accentColor: cfg.color, width: 15, height: 15 }}
                                        />
                                        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>
                                            {cfg.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {error && (
                        <div style={{
                            marginTop: 12, padding: '10px 14px',
                            background: '#fee2e2', border: '1px solid #fca5a5',
                            borderRadius: 8, color: '#dc2626', fontSize: 13,
                        }}>{error}</div>
                    )}
                </div>

                {/* footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #f3f4f6',
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                }}>
                    <button onClick={onHide} style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        padding: '8px 18px', fontSize: 13, fontWeight: 600,
                        color: '#374151', cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                        {alreadyUpdated ? 'Close' : 'Cancel'}
                    </button>
                    {!alreadyUpdated && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                background: saving ? '#a5b4fc' : '#6366f1',
                                border: 'none', borderRadius: 8, padding: '8px 22px',
                                fontSize: 13, fontWeight: 700, color: '#fff',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                            }}>
                            {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Request Card ───────────────────────────────────────── */
const RequestCard = ({ req, onUpdate, isDark }) => {
    const cardBg = isDark ? '#1a2132' : '#fff';
    const border = isDark ? '#2d3748' : '#e5e7eb';
    const text   = isDark ? '#f3f4f6' : '#111827';
    const muted  = isDark ? '#9ca3af' : '#6b7280';

    return (
        <div style={{
            background: cardBg, border: `1px solid ${border}`, borderRadius: 10,
            padding: '16px 18px', marginBottom: 12,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'box-shadow .2s',
        }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = isDark ? '0 4px 18px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.09)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)'}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#6366f1' }}>
                            {req.indentNumber}
                        </span>
                        <StatusPill status={req.status} />
                        <span style={{
                            fontSize: 11, color: muted,
                            background: isDark ? '#252d3d' : '#f3f4f6',
                            padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                        }}>
                            FY {req.fin_year}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                        Outlet:{' '}
                        <span style={{ color: text, fontWeight: 500 }}>
                            {req?.outlet?.name || req?.outlet?._id || '—'}, {req?.outlet?.city}
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ textAlign: 'right', fontSize: 12, color: muted }}>
                        <div>Created: <span style={{ color: text }}>{fmt(req.createdAt)}</span></div>
                        <div style={{ marginTop: 2 }}>Updated: <span style={{ color: text }}>{fmt(req.updatedAt)}</span></div>
                    </div>
                    <button onClick={() => onUpdate(req)} style={{
                        background: '#f5f3ff', border: '1px solid #ddd6fe',
                        borderRadius: 7, padding: '6px 13px',
                        fontSize: 12, fontWeight: 700, color: '#7c3aed',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                        display: 'flex', alignItems: 'center', gap: 5,
                        fontFamily: 'inherit', transition: 'background .15s',
                    }}
                        onMouseEnter={e => e.currentTarget.style.background = '#ede9fe'}
                        onMouseLeave={e => e.currentTarget.style.background = '#f5f3ff'}
                    >
                        ✎ Update
                    </button>
                </div>
            </div>

            <ItemsRow items={req.items || []} />
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────── */
const StocksRequest = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin]               = useState(currentSkin);
    const accessToken                   = localStorage.getItem('authToken');

    const [stockRequests, setStockRequests] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null); // OutletSelect returns option object { value, label }
    const [loading, setLoading]             = useState(false);
    const [error, setError]                 = useState('');
    const [searchTerm, setSearchTerm]       = useState('');
    const [statusFilter, setStatusFilter]   = useState('ALL');
    const [updateTarget, setUpdateTarget]   = useState(null);

    const isDark    = skin === 'dark';
    const pageBg    = isDark ? '#111827' : '#f3f4f6';
    const cardBg    = isDark ? '#1a2132' : '#fff';
    const textMain  = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';
    const borderCol = isDark ? '#2d3748' : '#e5e7eb';
    const inputBg   = isDark ? '#1e293b' : '#fff';

    /* ── fetch stock requests ── */
    const getStockRequests = async (outletOption) => {
        // outletOption: OutletSelect option object { value, label } | null | undefined
        const option   = outletOption !== undefined ? outletOption : selectedOutlet;
        const outletId = option?.value || option?._id || '';
        setLoading(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const params  = outletId ? `?outlet=${outletId}` : '';
            const res = await axiosInstance.get(`stock-requisition${params}`, { headers });
            const raw = res?.data?.data?.data ?? [];
            console.log("Response of Stock Request", raw);
            const formattedData = Array.isArray(raw) ? raw : [raw].filter(Boolean);
            // ✅ Latest First (NEW DATA ON TOP)
            const sortedData = formattedData?.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );

            setStockRequests(sortedData);
        } catch (err) {
            console.error('Stock requests fetch error:', err);
            setError('Failed to load stock requests. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { getStockRequests(''); }, []);

    const handleOutletChange = (option) => {
        setSelectedOutlet(option);
        getStockRequests(option);
    };

    /* optimistic UI update after modal save */
    const handleUpdated = (id, newStatus) => {
        setStockRequests(prev =>
            prev.map(r => r._id === id
                ? { ...r, status: newStatus, updatedAt: new Date().toISOString() }
                : r)
        );
    };

    const filtered = stockRequests.filter(req => {
        const matchSearch =
            !searchTerm ||
            req.indentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.outlet?.outletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.items?.some(i => i.rawMaterial?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchStatus = statusFilter === 'ALL' || req.status === statusFilter;
        return matchSearch && matchStatus;
    });

    const counts = stockRequests.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

    return (
        <React.Fragment>
            <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

            <Header onSkin={setSkin} />

            {updateTarget && (
                <UpdateModal
                    request={updateTarget}
                    onHide={() => setUpdateTarget(null)}
                    onUpdated={handleUpdated}
                    accessToken={accessToken}
                />
            )}

            <div className={`main main-app p-3 p-lg-3 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}
                style={{ minHeight: '100vh', background: pageBg, fontFamily: "'Nunito', 'Segoe UI', sans-serif" }}>
                <Container fluid>

                    {/* ── Page Header ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <h4 style={{ fontWeight: 800, fontSize: 21, margin: 0, color: textMain, letterSpacing: '-0.01em' }}>
                                Stock Requests
                            </h4>
                            <p style={{ color: textMuted, margin: '3px 0 0', fontSize: 13 }}>
                                Manage and track material requisitions from outlets
                            </p>
                        </div>
                        <button onClick={() => getStockRequests()} disabled={loading} style={{
                            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 9,
                            padding: '9px 18px', fontWeight: 700, fontSize: 13,
                            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                        }}>
                            {loading
                                ? <><Spinner animation="border" size="sm" /> Refreshing…</>
                                : <><span style={{ fontSize: 15 }}>↺</span> Refresh</>}
                        </button>
                    </div>

                    {/* ── Outlet Filter + Search ── */}
                    <div style={{
                        background: cardBg, border: `1px solid ${borderCol}`,
                        borderRadius: 10, padding: '12px 16px', marginBottom: 14,
                        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}>
                        {/* OutletSelect */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ minWidth: 300 }}>
                                <Form.Group controlId="outletSelect" style={{ margin: 0 }}>
                                    <OutletSelect
                                        value={selectedOutlet}
                                        onChange={handleOutletChange}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div style={{ width: 1, height: 50, background: borderCol }} />

                        {/* Search */}
                        <div style={{ minWidth: 300, flex: 1 }}>
                            <Form.Label className="fw-semibold" style={{ color: textMuted, marginBottom: 4 }}>
                                Search
                            </Form.Label>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: isDark ? '#252d3d' : '#f9fafb',
                                border: `1px solid ${borderCol}`, borderRadius: 8, padding: '6px 12px',
                            }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Search by indent no., outlet, or material…"
                                    style={{
                                        border: 'none', background: 'transparent', flex: 1,
                                        outline: 'none', color: textMain, fontSize: 13, fontFamily: 'inherit',
                                    }}
                                />
                                {searchTerm && (
                                    <button onClick={() => setSearchTerm('')} style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: textMuted, fontSize: 18, lineHeight: 1, padding: 0,
                                    }}>×</button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Status Filter Chips ── */}
                    {!loading && stockRequests.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                            {[
                                { key: 'ALL', label: 'All' },
                                ...Object.keys(STATUS_CONFIG).map(k => ({ key: k, label: STATUS_CONFIG[k].label }))
                            ].map(({ key, label }) => {
                                const count  = key === 'ALL' ? stockRequests.length : (counts[key] || 0);
                                const cfg    = STATUS_CONFIG[key] || {};
                                const active = statusFilter === key;
                                return (
                                    <button key={key} onClick={() => setStatusFilter(key)} style={{
                                        border: `1.5px solid ${active ? (cfg.border || '#a5b4fc') : borderCol}`,
                                        background: active ? (cfg.bg || '#eef2ff') : cardBg,
                                        color: active ? (cfg.color || '#6366f1') : textMuted,
                                        borderRadius: 20, padding: '4px 13px', fontSize: 12,
                                        fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
                                        fontFamily: 'inherit',
                                    }}>
                                        {label} ({count})
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* ── Error ── */}
                    {error && (
                        <div style={{
                            background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 9,
                            padding: '11px 15px', marginBottom: 14, color: '#dc2626', fontSize: 13,
                        }}>{error}</div>
                    )}

                    {/* ── Loading Skeleton ── */}
                    {loading && (
                        <>
                            <style>{`@keyframes shimmer{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{
                                    background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10,
                                    padding: '16px 18px', marginBottom: 12,
                                    animation: 'shimmer 1.5s ease-in-out infinite',
                                }}>
                                    <div style={{ height: 14, width: '32%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5, marginBottom: 10 }} />
                                    <div style={{ height: 11, width: '20%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5 }} />
                                </div>
                            ))}
                        </>
                    )}

                    {/* ── Empty State ── */}
                    {!loading && !error && filtered.length === 0 && (
                        <div style={{
                            background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10,
                            padding: '52px 20px', textAlign: 'center',
                        }}>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: textMain, marginBottom: 5 }}>
                                {searchTerm || statusFilter !== 'ALL' ? 'No matching requests' : 'No stock requests found'}
                            </div>
                            <div style={{ color: textMuted, fontSize: 13 }}>
                                {searchTerm || statusFilter !== 'ALL'
                                    ? 'Try adjusting your search or filters'
                                    : selectedOutlet?.value
                                        ? 'This outlet has no stock requests yet'
                                        : 'Stock requisitions will appear here once raised'}
                            </div>
                        </div>
                    )}

                    {/* ── Request Cards ── */}
                    {!loading && filtered.map(req => (
                        <RequestCard
                            key={req._id}
                            req={req}
                            isDark={isDark}
                            onUpdate={(r) => setUpdateTarget(r)}
                        />
                    ))}

                    {/* ── Footer Count ── */}
                    {!loading && filtered.length > 0 && (
                        <div style={{ textAlign: 'center', color: textMuted, fontSize: 12, marginTop: 6 }}>
                            Showing {filtered.length} of {stockRequests.length} request{stockRequests.length !== 1 ? 's' : ''}
                        </div>
                    )}

                </Container>
            </div>
        </React.Fragment>
    );
};

export default StocksRequest;